<template>
  <div
    ref="heroRef"
    class="hero-banner"
    :class="{ 'hero-banner--visible': isVisible }"
    @mousemove="handleMouseMove"
    @mouseleave="resetImagePosition"
  >
    <img
      :src="currentImage"
      alt="Главный баннер"
      class="banner-image"
      :style="imageStyle"
    />

    <div class="ambient ambient-1"></div>
    <div class="ambient ambient-2"></div>
    <div class="ambient ambient-3"></div>

    <div class="buttons-wrapper">
      <button
        class="primary-button"
        type="button"
        @click="$emit('start-work-click')"
      >
        <span>Начать работать</span>

        <svg
          class="button-arrow"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 9H14M10 5L14 9L10 13"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <button
        class="secondary-button"
        type="button"
        @click="$emit('advantages-click')"
      >
        Узнать преимущества
      </button>
    </div>

    <div class="slogan">
      Рекрутинговый сервис для талантливых
      <br />
      специалистов
    </div>
  </div>

  <div class="separator"></div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
} from "vue";

defineEmits([
  "start-work-click",
  "advantages-click",
]);

const lightImage = new URL(
  "../assets/hero-light.webp",
  import.meta.url
).href;

const darkImage = new URL(
  "../assets/hero-dark.webp",
  import.meta.url
).href;

const heroRef = ref(null);
const isVisible = ref(false);

const imageTranslateX = ref(0);
const imageTranslateY = ref(0);
const imageRotateX = ref(0);
const imageRotateY = ref(0);

const theme = ref(
  document.documentElement.getAttribute("data-theme") ||
    "light"
);

const currentImage = computed(() => {
  return theme.value === "dark"
    ? darkImage
    : lightImage;
});

const imageStyle = computed(() => ({
  transform: `
    perspective(1200px)
    translate3d(
      ${imageTranslateX.value}px,
      ${imageTranslateY.value}px,
      0
    )
    rotateX(${imageRotateX.value}deg)
    rotateY(${imageRotateY.value}deg)
    scale(1.01)
  `,
}));

let observer;

const handleMouseMove = (event) => {
  if (window.innerWidth <= 768) {
    return;
  }

  const hero = heroRef.value;

  if (!hero) {
    return;
  }

  const rect = hero.getBoundingClientRect();

  const relativeX =
    (event.clientX - rect.left) / rect.width;

  const relativeY =
    (event.clientY - rect.top) / rect.height;

  imageTranslateX.value =
    (relativeX - 0.5) * 4;

  imageTranslateY.value =
    (relativeY - 0.5) * 8;

  imageRotateY.value =
    (relativeX - 0.5) * 1.8;

  imageRotateX.value =
    (0.5 - relativeY) * 1.4;
};

const resetImagePosition = () => {
  imageTranslateX.value = 0;
  imageTranslateY.value = 0;
  imageRotateX.value = 0;
  imageRotateY.value = 0;
};

onMounted(() => {
  observer = new MutationObserver(() => {
    theme.value =
      document.documentElement.getAttribute(
        "data-theme"
      ) || "light";
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  requestAnimationFrame(() => {
    isVisible.value = true;
  });
});

onUnmounted(() => {
  observer?.disconnect();
});
</script>

<style scoped>

.hero-banner {
  width: 900px;
  height: 750px;

  margin: 0 auto;

  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  opacity: 0;
  transform: translateY(18px);

  transition:
    opacity 0.8s ease,
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-banner--visible {
  opacity: 1;
  transform: translateY(0);
}

.banner-image {
  width: 100%;
  height: auto;

  display: block;

  object-fit: cover;
  transition:
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.3s ease,
    opacity 0.3s ease;

  will-change: transform;
}

.buttons-wrapper {
  position: absolute;

  bottom: 140px;
  left: -90px;

  display: flex;
  gap: 14px;
}

.primary-button,
.secondary-button {
  min-height: 48px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0 24px;

  border-radius: 999px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 16px;
  letter-spacing: -0.8px;

  cursor: pointer;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease,
    background-color 0.2s ease;
}

.primary-button {
  position: relative;
  overflow: hidden;

  gap: 10px;

  color: white;

  border: 1px solid rgba(255, 255, 255, 0.18);

  background:
    linear-gradient(
      135deg,
      #3b8df5 0%,
      #2277e6 58%,
      #1764d2 100%
    );

  box-shadow:
    0 10px 26px rgba(34, 119, 230, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.primary-button::before {
  content: "";

  position: absolute;

  top: -80%;
  left: -60%;

  width: 45%;
  height: 260%;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.34),
      transparent
    );

  transform: rotate(18deg);

  transition: left 0.7s ease;
}

.primary-button:hover {
  transform: translateY(-2px);

  box-shadow:
    0 14px 32px rgba(34, 119, 230, 0.34),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.primary-button:hover::before {
  left: 120%;
}

.primary-button:active {
  transform: scale(0.97);
}

.button-arrow {
  transition: transform 0.2s ease;
}

.primary-button:hover .button-arrow {
  transform: translateX(3px);
}

.secondary-button {
  color: #111111;

  border: 1px solid rgba(0, 0, 0, 0.04);

  background:
    linear-gradient(
      135deg,
      #f7c9d3 0%,
      #f4bbc7 100%
    );

  box-shadow:
    0 9px 24px rgba(244, 187, 199, 0.22);
}

.secondary-button:hover {
  transform: translateY(-2px);

  box-shadow:
    0 13px 30px rgba(244, 187, 199, 0.32);
}

.secondary-button:active {
  transform: scale(0.97);
}

.slogan {
  position: absolute;

  bottom: 80px;
  left: -90px;

  max-width: 500px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 20px;
  line-height: 1.08;
  letter-spacing: -0.8px;

  color: var(--text);
}

.separator {
  width: 100%;
  height: 1px;

  position: relative;
  z-index: 10;

  flex-shrink: 0;

  background-color: var(--separator_home);
}

@media (max-width: 1100px) {
  .hero-banner {
    width: min(900px, calc(100% - 48px));
  }

  .buttons-wrapper,
  .slogan {
    left: 0;
  }
}

@media (max-width: 768px) {
  .hero-banner {
    width: 100%;
    height: auto;

    margin-top: 10px;
    padding: 0 12px 28px;

    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .banner-image {
    width: 100%;
    border-radius: 16px;

    opacity: 0.22;

    transform: none !important;
    box-shadow: none;
  }

  .buttons-wrapper {
    position: static;

    width: 100%;

    margin-top: 14px;

    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .primary-button,
  .secondary-button {
    width: 360px;
    min-height: 48px;

    padding: 0 18px;

    font-size: 14px;
    border-radius: 16px;
  }

  .slogan {
    position: static;

    width: 100%;

    margin-top: 16px;

    font-size: 16px;
    line-height: 1.25;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-banner,
  .banner-image,
  .primary-button,
  .secondary-button,
  .button-arrow {
    transition-duration: 0.01ms !important;
  }

  .banner-image {
    transform: none !important;
  }
}


.secondary-button {
  position: relative;
  overflow: hidden;

  gap: 10px;

  color: #111111;

  border: 1px solid rgba(0, 0, 0, 0.05);

  background:
    linear-gradient(
      135deg,
      #f8ccd5 0%,
      #f4bbc7 58%,
      #efacba 100%
    );

  box-shadow:
    0 9px 24px rgba(244, 187, 199, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.secondary-button::before {
  content: "";

  position: absolute;

  top: -80%;
  left: -60%;

  width: 45%;
  height: 260%;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );

  transform: rotate(18deg);

  transition: left 0.7s ease;
}

.secondary-button:hover {
  transform: translateY(-2px);

  box-shadow:
    0 13px 30px rgba(244, 187, 199, 0.34),
    inset 0 1px 0 rgba(255, 255, 255, 0.38);
}

.secondary-button:hover::before {
  left: 120%;
}

.secondary-button:active {
  transform: scale(0.97);

  box-shadow:
    0 6px 16px rgba(244, 187, 199, 0.22);
}


.ambient{
    position:absolute;

    border-radius:50%;
    pointer-events:none;

    filter:blur(90px);

    opacity:.18;

    mix-blend-mode:screen;

    animation:floatAmbient 18s ease-in-out infinite;
}

.ambient-1{
    width:320px;
    height:320px;

    left:6%;
    top:8%;

    background:#5A8BFF;
}

.ambient-2{
    width:280px;
    height:280px;

    right:8%;
    top:18%;

    background:#8B5CF6;

    animation-delay:-6s;
}

.ambient-3{
    width:240px;
    height:240px;

    left:42%;
    bottom:8%;

    background:#3B82F6;

    animation-delay:-11s;
}

@keyframes floatAmbient{

    0%{
        transform:
            translate3d(0,0,0)
            scale(1);
    }

    25%{
        transform:
            translate3d(20px,-14px,0)
            scale(1.06);
    }

    50%{
        transform:
            translate3d(-18px,12px,0)
            scale(.96);
    }

    75%{
        transform:
            translate3d(16px,22px,0)
            scale(1.03);
    }

    100%{
        transform:
            translate3d(0,0,0)
            scale(1);
    }

}

[data-theme="light"] .ambient{
    opacity:.12;
}

</style>